import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { LIVE_EVENT_STATUS_BY_NUMBER } from '../entities/academy-enums.model';
import { MineSubscriptionsQuery } from '../entities/academy-query.model';
import { LiveSubscriptionResponse } from '../entities/academy.model';
import {
  MineResult,
  MyLiveSubscriptionSummary,
  toMineResult,
} from '../entities/academy-responses.model';

/**
 * Subscribe / unsubscribe to a live event. 🔑 identity from token (no `PersonId`).
 * ⚠ subscribing to an Ended/Cancelled/Recorded event yields an unmapped **500** —
 * surfaced raw; the consumer maps the "event closed" copy (no data-layer i18n, per T093).
 */
@Injectable({ providedIn: 'root' })
export class LiveSubscriptionsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;

  /**
   * `GET /LiveSubscriptions/mine` — lives the student saved.
   * `scope` defaults to `upcoming` (from now on); pass `'all'` to include past events.
   * `Status === 'Recorded'` → `VideoAssetId` non-null; can link straight to the recording.
   */
  public mine(query: MineSubscriptionsQuery = {}): Observable<MineResult<MyLiveSubscriptionSummary>> {
    let params = new HttpParams();
    if (query.scope) params = params.set('scope', query.scope);
    if (query.top != null) params = params.set('top', String(query.top));
    if (query.skip != null) params = params.set('skip', String(query.skip));
    return this.http
      .get<unknown>(`${this.baseUrl}/LiveSubscriptions/mine`, {
        params,
        withCredentials: true,
      })
      .pipe(map((res) => this.coerceStatus(toMineResult<MyLiveSubscriptionSummary>(res))));
  }

  /**
   * `MyLiveSubscriptionSummary.Status` is a `LiveEventStatusType`, but the FIELD is
   * named `Status` — so the generic `fromWireEnums` (keyed by the enum's own field
   * name `LiveEventStatusType`) never coerces it. Translate the numeric wire code to
   * the name the union declares, so consumers can compare `Status === 'Recorded'`.
   * Already-named values pass through untouched.
   */
  private coerceStatus(
    result: MineResult<MyLiveSubscriptionSummary>,
  ): MineResult<MyLiveSubscriptionSummary> {
    return {
      ...result,
      items: result.items.map((item) => {
        const status = item.Status as unknown;
        return typeof status === 'number'
          ? { ...item, Status: LIVE_EVENT_STATUS_BY_NUMBER[status] ?? item.Status }
          : item;
      }),
    };
  }

  /** `POST /LiveSubscriptions/{id}/subscribe` — idempotent per (event, person). */
  public subscribe(liveEventId: string): Observable<LiveSubscriptionResponse> {
    return this.http.post<LiveSubscriptionResponse>(
      `${this.baseUrl}/LiveSubscriptions/${encodeURIComponent(liveEventId)}/subscribe`,
      {},
      { withCredentials: true },
    );
  }

  /** `DELETE /LiveSubscriptions/{id}/subscribe` — 204 on success, ⚠404 if none. */
  public unsubscribe(liveEventId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/LiveSubscriptions/${encodeURIComponent(liveEventId)}/subscribe`,
      { withCredentials: true },
    );
  }
}
